'use strict';

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {observer} from "mobx-react";
import classnames from 'classnames';
import {isEmpty as underscoreIsEmpty} from 'underscore';
import moment from "moment";

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';
import Badge from 'react-bootstrap/Badge';
import Card from 'react-bootstrap/Card';

import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import {allFalse, pluralize, isEmpty, daysOfWeekAbbreviation} from '@uw-it-sis/lib-react/lib/Utils';

import TermUtils from "@uw-it-sis/lib-react/lib/TermUtils";
import { normalize } from "./CourseUtils";
import notifyStore from './NotifyStore';
import SectionCode from "./SectionCode";
import notifySubscriptionFormStore from './NotifySubscriptionFormStore';


/**
 * End point checkbox widget.
 */
class NotifyEndPoint extends Component {

    constructor(props) {
        super(props);
        this.props = props;

        this.store = notifySubscriptionFormStore;

        this.onChange = this.onChange.bind(this);
    }

    onChange = (e) => {
        let property = e.target.name;
        let value = e.target.checked;
        this.store.updateFormData(property, value);
    };

    render() {
        // Get the end point for specified protocol
        let endpoint = notifyStore.getEndPoint(this.props.protocol);

        return (
            <Form.Group>
                <Form.Check id={'notify-endpoint-' + this.props.protocol}
                            type="checkbox"
                            inline>
                    <Form.Check.Input type="checkbox"
                                      name={'protocols:' + this.props.protocol}
                                      checked={this.props.subscribed}
                                      disabled={! endpoint}
                                      onChange={this.onChange}/>
                    <Form.Check.Label type="checkbox"
                                      className="fw-bold">
                        {this.props.title}
                    </Form.Check.Label>
                </Form.Check>
                <Form.Text className={classnames('fw-normal', 'mt-0', {'text-body': endpoint}, {'text-muted': ! endpoint})}>
                    {! endpoint ? 'None Saved' : endpoint.EndpointAddress}
                </Form.Text>
            </Form.Group>
        );
    }
}

NotifyEndPoint.propTypes = {
    title: PropTypes.string.isRequired,
    protocol: PropTypes.string.isRequired,
    subscribed: PropTypes.bool.isRequired
};


/**
 * The form which appears in the Notify popup.
 */
class NotifySubscriptionForm extends Component {

    constructor(props) {
        super(props);
        this.props = props;

        this.store = notifySubscriptionFormStore;

        this.onSubmit = this.onSubmit.bind(this);
        this.onDelete = this.onDelete.bind(this);
    }

    componentDidMount() {
        // Set the form data with the activityId of the section selected and figure out if protocols are already subscribed
        this.store.mount(this.props.section.activityId);
        if (this.props.additionalAnalytics) {
            this.store.setAdditionalAnalytics(this.props.additionalAnalytics);
        }
    }

    onSubmit = (e) => {
        e.preventDefault();  //  Prevent POST and reload.
        e.stopPropagation();
        // Submit the form data for add or update subscription
        this.store.subscription(this.props.subscribed);
        notifyStore.hideSectionSubscribeForm();
    };

    onDelete = (e) => {
        e.preventDefault();  //  Prevent POST and reload.
        e.stopPropagation();
        // Submit the form data for remove subscription
        this.store.deleteSubscription(this.props.subscribed);
        notifyStore.hideSectionSubscribeForm();
    };

    render() {
        let section = this.props.section;
        let course = this.props.course;
        let formData = this.store.formData;
        let remove = null;

        if (this.props.subscribed) {
            remove = <Button type="submit" variant="secondary" onClick={this.onDelete}>Unsubscribe</Button>;
        }

        // Don't show negative numbers for available seats since sometimes counts are larger than maximums
        let availableSeats = Math.max(0, (section.enrollMaximum - section.enrollCount));





        let meetings;
        if (section.hasOwnProperty("meetingDetailsList") && section.meetingDetailsList.length > 0) {
            meetings = section.meetingDetailsList.map((offering, i) => {
                // If days and time are empty, its TBA
                let daysAndTimesEmpty = true;
                [offering.days, offering.time].forEach(test => {
                    if ( daysAndTimesEmpty && ! (isEmpty(test) || underscoreIsEmpty(test.trim())) ) {
                        daysAndTimesEmpty = false;
                    }
                });

                if (daysAndTimesEmpty) {
                    return (
                        <Row key={i} className="g-0">
                            <Col>Meeting time and location: --</Col>
                        </Row>
                    );
                } else {
                    let location;
                    if (offering.building) {
                        location = <>{offering.building} <span className="sr-only"> building room </span> {offering.room}</>;
                    }

                    let time = offering.time;
                    if ( /\d{1,2}:\d{2}\s[apmAPM]{2}\s\W\s\d{1,2}:\d{2}\s[apmAPM]{2}/.test(time) ) {
                        let times = time.split(" - ");
                        time =
                            <>
                                <time dateTime={moment(times[0], "h:mm A").format("HH:mm")}>{times[0]}</time>
                                <span> - </span>
                                <time dateTime={moment(times[1], "h:mm A").format("HH:mm")}>{times[1]}</time>
                            </>
                    }

                    return (
                        <Row key={i} className="g-0 d-inline-block d-sm-flex">
                            <Col sm={3} className="text-sm-start me-2 me-sm-0">{daysOfWeekAbbreviation(offering.days)}</Col>
                            <Col sm={6} className="text-sm-center me-2 me-sm-0">{time}</Col>
                            <Col sm={3} className="text-sm-end">{location}</Col>
                        </Row>
                    );
                }
            });
        }

        // Build array of all instructors names without duplicates
        let allInstructors = section.allInstructors.map(instructor => instructor.name).filter((v, i, s) => s.indexOf(v) === i);
        let instructor;
        if (allInstructors.length > 0) {
            instructor =
                <Row className="g-0">
                    <Col>{pluralize("Instructor", allInstructors)}: {allInstructors.join(", ")}</Col>
                </Row>;
        }

        let summerBadge;
        if (TermUtils.makeTermName(section.atpId).toUpperCase() === TermUtils.TERMS[3].toUpperCase() && section.summerTerm ) {
            summerBadge =
                <>
                    <span className="sr-only">Summer Term: </span>
                    <Badge bg="light-gray" className="text-dark fs-9 text-uppercase fw-normal border ms-2">{section.summerTerm}</Badge>
                </>;
        }

        let enrollStatus;
        if ( section.enrollStatus ) {
            let enrollStatusValue;
            switch ( section.enrollStatus.toLowerCase() ) {
                case 'open':
                case 'closed':
                    enrollStatusValue = section.enrollStatus;
                    break;
                default:
                    enrollStatusValue = "--";
            }

            let enrollBadgeVariant;
            switch (enrollStatusValue) {
                case "closed":
                    enrollBadgeVariant = "danger";
                    break;
                case "open":
                    enrollBadgeVariant = "success";
                    break;
                default:
                    enrollBadgeVariant = "white";
            }

            enrollStatus =
                <Badge bg={enrollBadgeVariant} className="fs-9 text-uppercase">
                    {enrollStatusValue}
                </Badge>;
        }

        return (
            <form>
                <Card border="border">
                    <Card.Body>
                        <div className="mb-2">
                            <h3 className="h4 mb-0 d-inline align-middle">
                                {normalize(course.code)}
                            </h3>
                            <Badge bg="light-gray" className="text-dark align-middle fs-9 text-uppercase fw-normal border ms-2" aria-hidden="true">{course.credit} <abbr title="Credit">CR</abbr></Badge>
                            <div className="lead me-4">
                                {course.courseTitle}
                            </div>
                            <span className="sr-only">{course.credit} {pluralize("Credit", course.credit)}</span>
                        </div>
                        <div className="flex-grow-1">
                            <div className="d-flex align-items-start">
                                <div className="sectionCode text-nowrap" style={{width: "48px", minWidth: "48px"}}>
                                    <SectionCode section={section} />
                                </div>
                                <Row className="g-0 flex-grow-1 d-flex align-items-center">
                                    <Col xs={12} sm={8}>
                                        <span className="sr-only"><abbr title="Schedule Line Number">SLN</abbr>: </span>
                                        <a href={`https://sdb.admin.washington.edu/timeschd/uwnetid/sln.asp?QTRYR=${section.qtryr}&SLN=${section.registrationCode}`}
                                           className="align-middle me-2"
                                           target="_blank">
                                            {section.registrationCode}
                                        </a>
                                        <span className="sr-only">Section Type: </span>
                                        <Badge bg="light-gray" className="text-dark align-middle fs-9 text-uppercase fw-normal border">
                                            {section.activityOfferingType}
                                        </Badge>
                                        {summerBadge}
                                    </Col>
                                    <Col xs={12} sm={4} className="text-sm-end text-nowrap">
                                        <span className="sr-only">Enrollment Status: </span>
                                        {enrollStatus}
                                        <Badge bg="light-gray" className="text-dark fs-9 text-uppercase fw-normal border">
                                            <span>{section.enrollCount} </span>
                                            <span className="sr-only">seats out </span>
                                            <span>of {section.enrollMaximum}</span>
                                            <span className="sr-only">seats</span>
                                        </Badge>
                                    </Col>
                                </Row>
                            </div>
                            <div className="mt-2" style={{paddingLeft: "48px"}}>
                                {meetings}
                                {instructor}
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <Row className="my-4 mx-0">
                    <Col xs={6}><NotifyEndPoint title="SMS / text" protocol="sms" subscribed={formData.protocols.sms} /></Col>
                    <Col xs={6}><NotifyEndPoint title="E-mail" protocol="email" subscribed={formData.protocols.email} /></Col>
                </Row>
                <div className="text-start"><a href={appStore.getNotifyUrl()} target="_blank" className="small">Manage all notifications and settings on Notify.UW</a></div>
                <ButtonToolbar className="mt-3 justify-content-end">
                    {remove}
                    <Button type="submit" variant="primary" onClick={this.onSubmit}
                            disabled={allFalse(formData.protocols)}>{this.props.subscribed ? 'Update':'Subscribe'}</Button>
                </ButtonToolbar>
            </form>
        );
    }
}

NotifySubscriptionForm.propTypes = {
    course: PropTypes.object.isRequired,
    section: PropTypes.object.isRequired,
    subscribed: PropTypes.bool.isRequired,
    additionalAnalytics: PropTypes.object
};

export default observer(NotifySubscriptionForm);